import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { Analytics } from "@vercel/analytics/react";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Feedback from "./pages/Feedback";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Activity from "./pages/Activity";
import TrailExplorer from "./pages/TrailExplorer";
import TrailExplorer2 from "./pages/TrailExplorer2";
import TrailDetail from "./pages/TrailDetail";
import RouteDetail from "./pages/RouteDetail";
import RunDetail from "./pages/RunDetail";
import RunEdit from "./pages/RunEdit";
import FeatureDetail from "./pages/FeatureDetail";
import UploadRun from "./pages/UploadRun";
import LogRun from "./pages/LogRun";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import CreateEvent from "./pages/CreateEvent";
import EditEvent from "./pages/EditEvent";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import Onboarding from "./pages/Onboarding";
import GpxTracksGuide from "./pages/GpxTracksGuide";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Unauthenticated Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            
            {/* Checkout Routes */}
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            
            {/* Legacy auth routes - redirect to /auth */}
            <Route path="/login" element={<Auth />} />
            <Route path="/signup" element={<Auth />} />
            
            {/* Onboarding */}
            <Route path="/onboarding" element={<Onboarding />} />
            
            {/* Help pages */}
            <Route path="/help/gpx-tracks" element={<GpxTracksGuide />} />
            
            {/* Authenticated Routes */}
            <Route path="/explore" element={<TrailExplorer2 />} />
            <Route path="/explore-mapbox" element={<TrailExplorer />} />
            <Route path="/home" element={<Home />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/trails/:trailId" element={<TrailDetail />} />
            <Route path="/routes/:routeId" element={<RouteDetail />} />
            <Route path="/runs/:runId" element={<RunDetail />} />
            <Route path="/runs/:runId/edit" element={<RunEdit />} />
            <Route path="/feature/:featureId" element={<FeatureDetail />} />
            <Route path="/upload/run" element={<UploadRun />} />
            <Route path="/log-run" element={<LogRun />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            
            {/* Event Routes */}
            <Route path="/events" element={<Events />} />
            <Route path="/events/create" element={<CreateEvent />} />
            <Route path="/events/:eventId" element={<EventDetail />} />
            <Route path="/events/:eventId/edit" element={<EditEvent />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Analytics />
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
